import { UpgradeSubscriptionModal } from '@components'

import { Button, ItemsPerPage, Table, useModal, withModalConditionalRender } from '@billy/design-system'
import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { AttachmentPreviewModal as AttachmentPreviewModalComponent } from '@features/attachments/elements/AttachmentPreviewModal'
import { useFetchAttachment } from '@features/attachments/hooks/useFetchAttachment'
import { useAccounts } from '@modules-deprecated/app/accounts'
import { useUserOrganization } from '@modules-deprecated/app/organization'
import { useTaxRates } from '@modules-deprecated/app/taxrates'

import { ModalId } from '../../../../../../enums/modalId'
import { Scope } from '../../../../../../enums/scope'
import { TableIds } from '../../../../../../enums/tableIds'
import { TrackingContext } from '../../../../../../enums/trackingContext'
import { useSegment } from '../../../../../../hooks'
import { isAuthorized } from '../../../../../../utils/isAuthorized'
import { useVatSuggestionsForm } from './contexts/vatSuggestionsFormContext'
import { SuggestionsTableColumns } from './elements/TableColumns'
import { useTableData } from './hooks/useTableData'
import { BlurredOverlay, ContentWrapper, TableWrapper } from './styles'

const AttachmentPreviewModal = withModalConditionalRender(AttachmentPreviewModalComponent)

const SUGGESTIONS_ATTACHMENT_PREVIEW_MODAL_ID = `${ModalId.AttachmentPreviewModal}-suggestions-table`
const UPGRADE_SUBSCRIPTION_MODAL_ID = `${ModalId.UpgradeSubscriptionModal}-vat-suggestions-table`

interface SuggestionsTableProps {
  onBackButtonClick: () => void
}

export const SuggestionsTable = ({ onBackButtonClick }: SuggestionsTableProps) => {
  const { t } = useTranslation()
  const { track } = useSegment()
  const { accounts, isLoading: isAccountsLoading } = useAccounts()
  const { taxRates, isLoading: isTaxRatesLoading } = useTaxRates()
  const { organization } = useUserOrganization()
  const hasUpsellScope = isAuthorized(Scope.VatHelperUpsell)

  const [selectedAttachmentId, setSelectedAttachmentId] = useState<string | undefined>(undefined)
  const { attachment: selectedAttachment } = useFetchAttachment(selectedAttachmentId)
  const { open: openAttachmentModal, close: closeAttachmentModal } = useModal(SUGGESTIONS_ATTACHMENT_PREVIEW_MODAL_ID, {
    onClose: () => {
      setSelectedAttachmentId(undefined)
    },
  })
  const { open: openUpgradeModal } = useModal(UPGRADE_SUBSCRIPTION_MODAL_ID, {
    onClose: onBackButtonClick,
  })

  useEffect(() => {
    if (hasUpsellScope) {
      openUpgradeModal()
    }
  }, [hasUpsellScope, openUpgradeModal])

  const {
    Form,
    suggestions,
    isLoading: isSuggestionsLoading,
    pageSize,
    pagination,
    setPage,
    setPageSize,
    reviewedRowIds,
    handleReviewedChange,
    isRowReviewed,
  } = useVatSuggestionsForm()

  const isLoading = isAccountsLoading || isTaxRatesLoading || isSuggestionsLoading

  const handleAttachmentClick = useCallback(
    (attachmentId: string) => {
      setSelectedAttachmentId(attachmentId)
      openAttachmentModal()
    },
    [openAttachmentModal],
  )

  const footerButton = useMemo(
    () => (
      <Button fullWidth onClick={closeAttachmentModal} size="md">
        {t('close')}
      </Button>
    ),
    [closeAttachmentModal, t],
  )

  const columns = useMemo(
    () =>
      SuggestionsTableColumns({
        baseCurrency: organization?.baseCurrencyId || 'DKK',
        handleAttachmentClick,
        isRowReviewed,
        handleReviewedChange,
        track,
        t,
      }),
    [handleAttachmentClick, handleReviewedChange, isRowReviewed, organization?.baseCurrencyId, t, track],
  )

  const tableData = useTableData({ accounts, data: suggestions || [], locale: organization?.localeId, taxRates })

  const handlePageChange = useCallback(
    (newPage: number) => {
      setPage(newPage)
    },
    [setPage],
  )

  const handleItemsPerPageChange = useCallback(
    (newPageSize: ItemsPerPage) => {
      setPage(1)
      setPageSize(newPageSize)
    },
    [setPage, setPageSize],
  )

  return (
    <>
      {/* Unsafe solution, but decided by business to go with it for the first beta */}
      {hasUpsellScope && <BlurredOverlay />}
      <ContentWrapper hasUpsellScope={hasUpsellScope}>
        <Form>
          <TableWrapper reviewedRowIds={reviewedRowIds}>
            <Table
              id={TableIds.SuggestionsTable}
              columns={columns}
              currentPage={pagination?.page}
              data={tableData}
              isLoading={isLoading}
              itemsPerPage={pageSize}
              onItemsPerPageChange={handleItemsPerPageChange}
              onPageChange={handlePageChange}
              totalPageItems={pagination?.total}
              withPagination
              withItemsPerPageSelect
            />
          </TableWrapper>
        </Form>
      </ContentWrapper>
      {selectedAttachmentId && selectedAttachment && (
        <AttachmentPreviewModal
          attachment={selectedAttachment}
          id={SUGGESTIONS_ATTACHMENT_PREVIEW_MODAL_ID}
          footerButton={footerButton}
        />
      )}
      <UpgradeSubscriptionModal id={UPGRADE_SUBSCRIPTION_MODAL_ID} trackingContext={TrackingContext.VatHelper} />
    </>
  )
}
